import React from 'react';
import { View, Pressable, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Body, Caption, SubHeading } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';

type OfflineSectionCardProps = {
  title: string;
  icon: keyof typeof Ionicons.glyphMap;
  isDownloaded: boolean;
  isDownloading?: boolean;
  storageSize?: number;
  lastSynced?: number | null;
  onDownload?: () => void;
  onDelete?: () => void;
  description?: string;
  formatStorageSize?: (bytes: number) => string;
};

export function OfflineSectionCard({
  title,
  icon,
  isDownloaded,
  isDownloading = false,
  storageSize = 0,
  lastSynced,
  onDownload,
  onDelete,
  description,
  formatStorageSize,
}: OfflineSectionCardProps) {
  const theme = useTheme();

  const formatDate = (timestamp: number | null | undefined): string => {
    if (!timestamp) return 'Never';
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Unknown';
    }
  };

  const formatSize = (bytes: number): string => {
    if (formatStorageSize) {
      return formatStorageSize(bytes);
    }
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Card variant="outlined">
      <View style={{ gap: theme.spacing(2) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(2) }}>
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: theme.radii.lg,
              backgroundColor: isDownloaded 
                ? theme.colors.primary + '15' 
                : theme.colors.surface,
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: isDownloaded ? 2 : 1,
              borderColor: isDownloaded 
                ? theme.colors.primary 
                : theme.colors.borderMuted,
            }}
          >
            <Ionicons
              name={icon}
              size={24}
              color={isDownloaded ? theme.colors.primary : theme.colors.textSecondary}
            />
          </View>
          <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
            <SubHeading style={{ fontSize: 18 }}>{title}</SubHeading>
            {description && (
              <Caption color={theme.colors.textSecondary}>{description}</Caption>
            )}
          </View>
          {isDownloaded && (
            <View
              style={{
                paddingHorizontal: theme.spacing(1.5),
                paddingVertical: theme.spacing(0.5),
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.success + '20',
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
              }}
            >
              <Ionicons name="checkmark-circle" size={16} color={theme.colors.success || '#34C759'} />
              <Caption style={{ color: theme.colors.success || '#34C759', fontSize: 11, fontWeight: '600' }}>
                Available
              </Caption>
            </View>
          )}
        </View>

        {isDownloaded && (
          <View style={{ gap: theme.spacing(1) }}>
            {storageSize > 0 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="folder-outline" size={16} color={theme.colors.textSecondary} />
                <Caption color={theme.colors.textSecondary}>
                  Storage: {formatSize(storageSize)}
                </Caption>
              </View>
            )}
            {lastSynced && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="time-outline" size={16} color={theme.colors.textSecondary} />
                <Caption color={theme.colors.textSecondary}>
                  Last synced: {formatDate(lastSynced)}
                </Caption>
              </View>
            )}
          </View>
        )}

        <View style={{ flexDirection: 'row', gap: theme.spacing(1.5) }}>
          {isDownloaded ? (
            <>
              {onDelete && (
                <Button
                  label="Remove"
                  variant="secondary"
                  onPress={onDelete}
                  icon={<Ionicons name="trash-outline" size={18} color={theme.colors.error || '#FF3B30'} />}
                  style={{ flex: 1 }}
                />
              )}
            </>
          ) : (
            <>
              {onDownload && (
                <Button
                  label={isDownloading ? 'Downloading...' : 'Download for Offline'}
                  variant="primary"
                  onPress={onDownload}
                  disabled={isDownloading}
                  icon={
                    isDownloading ? (
                      <ActivityIndicator size="small" color={theme.colors.onPrimary} />
                    ) : (
                      <Ionicons name="download-outline" size={18} color={theme.colors.onPrimary} />
                    )
                  }
                  style={{ flex: 1 }}
                />
              )}
            </>
          )}
        </View>
      </View>
    </Card>
  );
}






